'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useCart } from '@/contexts/CartContext';
import { isAuthenticated } from '@/lib/apollo-client-simple';
import { useQuery } from '@apollo/client';
import { GET_CUSTOMER } from '@/lib/graphql-queries';

const steps = ['Contact', 'Shipping', 'Payment', 'Review'];

const WORDPRESS_URL = process.env.NEXT_PUBLIC_WORDPRESS_URL || '';
const IG_HANDLE = 'sweetskynbykym';

const FREE_SHIPPING_THRESHOLD = 12000;

const COUNTRIES = [
  { code: 'JM', name: 'Jamaica' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'OTHER', name: 'Other' },
];

const JAMAICA_PARISHES = [
  'Kingston', 'St. Andrew', 'St. Catherine', 'Clarendon', 'Manchester',
  'St. Elizabeth', 'Westmoreland', 'Hanover', 'St. James', 'Trelawny',
  'St. Ann', 'St. Mary', 'Portland', 'St. Thomas',
];

export default function CheckoutClient() {
  const router = useRouter();
  const [authChecked, setAuthChecked] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [promoCode, setPromoCode] = useState('');
  type AppliedCoupon = { code: string; discount_type: string; amount: number; free_shipping: boolean; minimum_amount: number; description?: string };
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const [contact, setContact] = useState({ email: '', phone: '' });
  const [shipping, setShipping] = useState({ country: 'JM', firstName: '', lastName: '', address: '', apt: '', city: '', state: '', zip: '' });

  // Checkout requires authentication — mirrors beauty-store pattern.
  // Without a token the REST endpoint returns 401; redirect to sign-in before rendering the form.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isAuthenticated()) {
      router.push('/sign-up-login?redirect=/checkout');
      return;
    }
    setAuthChecked(true);
  }, [router]);

  // Pull saved addresses from WC customer so returning users don't retype their
  // details on every order. Shipping takes priority for the ship-to form;
  // billing fills email/phone on the Contact step.
  const { data: customerData } = useQuery(GET_CUSTOMER, {
    skip: !authChecked,
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });
  const [prefillApplied, setPrefillApplied] = useState(false);
  useEffect(() => {
    if (prefillApplied || !customerData?.customer) return;
    const b = customerData.customer.billing;
    const s = customerData.customer.shipping;
    // Only prefill email/phone from billing if user hasn't typed anything yet.
    setContact((cur) => ({
      email: cur.email || b?.email || '',
      phone: cur.phone || b?.phone || '',
    }));
    const src = (s && (s.address1 || s.city)) ? s : b;
    if (src && (src.address1 || src.city)) {
      setShipping((cur) => ({
        country: cur.country || src.country || 'JM',
        firstName: cur.firstName || src.firstName || '',
        lastName: cur.lastName || src.lastName || '',
        address: cur.address || src.address1 || '',
        apt: cur.apt || src.address2 || '',
        city: cur.city || src.city || '',
        state: cur.state || src.state || '',
        zip: cur.zip || src.postcode || '',
      }));
    }
    setPrefillApplied(true);
  }, [customerData, prefillApplied]);

  const { items: cartContextItems, clearCart, removeFromCart } = useCart();
  const cartItems = cartContextItems.map((ci: any) => {
    const p = ci.product || {};
    const sizeMeta = Array.isArray(p.meta_data) ? p.meta_data.find((m: any) => m.key === 'size') : null;
    const priceStr = p.on_sale && p.sale_price ? p.sale_price : (p.price || p.regular_price || '0');
    const imageSrc =
      (Array.isArray(p.images) && p.images[0]?.src) ||
      p.image?.sourceUrl ||
      '/assets/images/kym-products/kym-jar-06.jpg';
    const imageAlt = (Array.isArray(p.images) && p.images[0]?.alt) || p.image?.altText || p.name || 'Product';
    // Real WC product ID, layered fallback:
    //   1. explicit product_id field (set by addToCart callers)
    //   2. numeric p.id (server-loaded carts carry the WC id in the id field)
    //   3. parse meta_data looking for product_id
    //   4. 0 → server will reject, user sees clear error
    let resolvedPid: number = 0;
    if (p.product_id) resolvedPid = Number(p.product_id) || 0;
    if (!resolvedPid && p.productId) resolvedPid = Number(p.productId) || 0;
    if (!resolvedPid && typeof p.id === 'number') resolvedPid = p.id;
    if (!resolvedPid) {
      const parsed = parseInt(String(p.id ?? ''), 10);
      if (!isNaN(parsed) && parsed > 0) resolvedPid = parsed;
    }
    return {
      id: String(p.id ?? ci.product?.id ?? ''),
      productId: resolvedPid,
      name: p.name || 'Product',
      size: sizeMeta?.value || '',
      price: parseFloat(String(priceStr).replace(/[^0-9.]/g, '')) || 0,
      qty: ci.quantity || 1,
      image: imageSrc,
      alt: imageAlt,
    };
  });

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.qty, 0);

  // Resolve discount from the applied coupon (returned by /validate-coupon).
  // percent + fixed_cart + fixed_product are the WC discount types we care about here.
  const couponDiscount = (() => {
    if (!appliedCoupon) return 0;
    if (appliedCoupon.discount_type === 'percent') return subtotal * (appliedCoupon.amount / 100);
    if (appliedCoupon.discount_type === 'fixed_cart') return appliedCoupon.amount;
    if (appliedCoupon.discount_type === 'fixed_product') return appliedCoupon.amount * cartItems.reduce((n, it) => n + it.qty, 0);
    return 0;
  })();

  const freeShippingByCoupon = !!appliedCoupon?.free_shipping;
  const freeShippingByThreshold = subtotal >= FREE_SHIPPING_THRESHOLD;
  const shippingCost = cartItems.length === 0 || freeShippingByCoupon || freeShippingByThreshold ? 0 : 9.99;
  const discount = couponDiscount;
  const total = Math.max(0, subtotal + shippingCost - discount);

  // POST code to the backend validator. Backend owns all rules (expiry, min, usage count, etc.)
  // so merchant can create/edit any WC coupon and it just works here.
  const handlePromo = async () => {
    const code = promoCode.trim();
    if (!code) return;
    setPromoError(null);
    setPromoChecking(true);
    try {
      const res = await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, subtotal }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.valid) {
        setAppliedCoupon(null);
        // Strip HTML that WC may embed in wc_price() output.
        const msg = typeof data?.message === 'string' ? data.message.replace(/<[^>]*>/g, '') : 'Invalid coupon.';
        setPromoError(msg);
        return;
      }
      setAppliedCoupon({
        code: data.code,
        discount_type: data.discount_type,
        amount: Number(data.amount) || 0,
        free_shipping: !!data.free_shipping,
        minimum_amount: Number(data.minimum_amount) || 0,
        description: data.description,
      });
    } catch (err: any) {
      setPromoError(err?.message || 'Could not validate coupon.');
    } finally {
      setPromoChecking(false);
    }
  };

  const clearCoupon = () => {
    setAppliedCoupon(null);
    setPromoError(null);
    setPromoCode('');
  };

  // Per-step validators. Each returns null when the step is complete or a
  // user-facing message for the missing/invalid field. Gates both the Next
  // button and the final Place Order click so a user can't rush through
  // with empty fields.
  const validateStep = (step: number): string | null => {
    if (step === 0) {
      if (!contact.email.trim()) return 'Please add your email so we can send you the order details.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) return 'Please check your email address — it should look like yourname@gmail.com';
      return null;
    }
    if (step === 1) {
      if (cartItems.length === 0) return 'Your bag is empty. Add a product before checking out.';
      if (!shipping.firstName.trim()) return 'Please add your first name.';
      if (!shipping.lastName.trim()) return 'Please add your last name.';
      if (!shipping.address.trim()) return 'Please add the street address where we should ship your order.';
      if (!shipping.city.trim()) return 'Please add your city or town.';
      if (!shipping.state.trim()) return shipping.country === 'JM' ? 'Please pick your parish from the list.' : 'Please add your state or province.';
      return null;
    }
    // Step 2 (Payment) is a method-confirmation step for COD — no fields to validate.
    return null;
  };

  const [stepError, setStepError] = useState<string | null>(null);

  const placeOrder = async () => {
    setSubmitting(true);
    setStepError(null);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('auth-token') : null;
      if (!token) {
        router.push('/sign-up-login?redirect=/checkout');
        return;
      }

      const billingAddress = {
        firstName: shipping.firstName,
        lastName: shipping.lastName,
        email: contact.email,
        phone: contact.phone,
        address: shipping.address,
        address2: shipping.apt,
        city: shipping.city,
        state: shipping.state,
        postalCode: shipping.zip,
        country: shipping.country,
      };

      const res = await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_method: 'cod',
          billing_address: billingAddress,
          shipping_address: billingAddress,
          coupon_code: appliedCoupon?.code || '',
          // Pass cart items directly — the headless frontend owns cart state
          // (localStorage via CartContext), so backend can't derive it from a
          // WC session. Structure matches what nexbeauty_process_checkout consumes.
          cart_items: cartItems.map((ci) => ({
            product_id: Number(ci.productId) || 0,
            quantity: ci.qty,
            size: ci.size,
          })),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        // Server's `message` is already user-friendly (e.g. "We couldn't find your items...");
        // a few operator-class failures like 401 bypass that with a generic wording.
        const userMsg = data?.message && !/^(rest_forbidden|unauthorized)/i.test(data?.code || '')
          ? data.message
          : 'We couldn\'t place your order. Please try again in a moment.';
        throw new Error(userMsg);
      }

      setOrderNumber(String(data.order_number || data.order_id || ''));
      setOrderPlaced(true);
      // Clear the cart on successful order placement so the bag icon reflects
      // post-purchase state and doesn't carry items into the next visit.
      clearCart();
    } catch (err: any) {
      setStepError(err?.message || 'We couldn\'t place your order. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    const err = validateStep(currentStep);
    if (err) {
      setStepError(err);
      return;
    }
    setStepError(null);
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      placeOrder();
    }
  };

  if (orderPlaced) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6">
        <div className="text-center max-w-lg">
          <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckIcon" size={32} className="text-accent" />
          </div>
          <h2 className="font-display text-4xl font-light text-foreground mb-3">Order Received</h2>
          <p className="text-sm font-semibold text-foreground mb-6">Order #{orderNumber}</p>
          <div className="bg-secondary border border-border rounded p-5 mb-6 text-left">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">Complete your payment</p>
            <p className="text-sm text-muted-foreground mb-3">
              DM us on Instagram <a href={`https://instagram.com/${IG_HANDLE}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground underline">@{IG_HANDLE}</a> with your order number <span className="font-semibold text-foreground">#{orderNumber}</span> to arrange payment.
            </p>
            <p className="text-sm text-muted-foreground">We&rsquo;ll confirm and ship within 24 hours.</p>
          </div>
          <p className="text-[12px] text-muted-foreground mb-8">A confirmation has been sent to {contact.email || 'your email'}.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/account?tab=orders" className="btn-primary">View Order</Link>
            <Link href="/products" className="btn-outline">Continue Shopping</Link>
          </div>
        </div>
      </div>);

  }

  if (!authChecked) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Checking sign-in…</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 pb-20">
      {/* Continue-shopping row — the site-wide sticky nav already renders the logo. */}
      <div className="flex items-center justify-end pt-6 mb-6">
        <Link href="/products" className="text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">
          ← Continue Shopping
        </Link>
      </div>

      {/* Progress steps */}
      <div className="flex items-center mb-10">
        {steps.map((step, idx) =>
        <React.Fragment key={step}>
            <div className="flex items-center gap-2">
              <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${
              idx < currentStep ? 'bg-accent text-accent-foreground' :
              idx === currentStep ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`
              }>
              
                {idx < currentStep ? <Icon name="CheckIcon" size={14} /> : idx + 1}
              </div>
              <span className={`text-[11px] font-semibold uppercase tracking-wider hidden sm:block ${idx === currentStep ? 'text-foreground' : 'text-muted-foreground'}`}>
                {step}
              </span>
            </div>
            {idx < steps.length - 1 &&
          <div className={`flex-1 h-px mx-3 transition-colors ${idx < currentStep ? 'bg-accent' : 'bg-border'}`} />
          }
          </React.Fragment>
        )}
      </div>

      <div className="grid lg:grid-cols-12 gap-12">
        {/* Form */}
        <div className="lg:col-span-7">
          {/* Step 0: Contact */}
          {currentStep === 0 &&
          <div className="space-y-5">
              <h2 className="font-display text-2xl font-light text-foreground mb-2">Contact Information</h2>
              <p className="text-[11px] text-muted-foreground mb-4">Fields marked with <span className="text-red-600">*</span> are required.</p>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">Email <span className="text-red-600">*</span></label>
                <input
                type="email"
                value={contact.email}
                onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                placeholder="you@example.com"
                className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring" />
              
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">Phone (optional)</label>
                <input
                type="tel"
                value={contact.phone}
                onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                placeholder="(555) 000-0000"
                className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring" />
              
              </div>
            </div>
          }

          {/* Step 1: Shipping */}
          {currentStep === 1 &&
          <div className="space-y-5">
              <h2 className="font-display text-2xl font-light text-foreground mb-6">Shipping Address</h2>

              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">Country</label>
                <select
                  value={shipping.country}
                  onChange={(e) => setShipping((s) => ({ ...s, country: e.target.value, state: '' }))}
                  className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring">
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
              { label: 'First Name', key: 'firstName', placeholder: shipping.country === 'JM' ? 'Kym' : 'First name', col: 1, required: true },
              { label: 'Last Name', key: 'lastName', placeholder: shipping.country === 'JM' ? 'Brown' : 'Last name', col: 1, required: true }].
              map((f) =>
              <div key={f.key}>
                    <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">
                      {f.label}{f.required && <span className="text-red-600"> *</span>}
                    </label>
                    <input
                  type="text"
                  value={shipping[f.key as keyof typeof shipping]}
                  onChange={(e) => setShipping((s) => ({ ...s, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring" />

                  </div>
              )}
              </div>
              {[
            { label: 'Street Address', key: 'address', placeholder: shipping.country === 'JM' ? '12 Knutsford Blvd' : '123 Main Street', full: true, required: true },
            { label: 'Apt / Suite (optional)', key: 'apt', placeholder: 'Apt 4B', full: true, required: false },
            { label: 'City / Town', key: 'city', placeholder: shipping.country === 'JM' ? 'Kingston' : 'City', full: false, required: true }].
            map((f) =>
            <div key={f.key}>
                  <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">
                    {f.label}{f.required && <span className="text-red-600"> *</span>}
                  </label>
                  <input
                type="text"
                value={shipping[f.key as keyof typeof shipping]}
                onChange={(e) => setShipping((s) => ({ ...s, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring" />

                </div>
            )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">
                    {shipping.country === 'JM' ? 'Parish' : 'State / Province'}<span className="text-red-600"> *</span>
                  </label>
                  {shipping.country === 'JM' ? (
                    <select
                      value={shipping.state}
                      onChange={(e) => setShipping((s) => ({ ...s, state: e.target.value }))}
                      className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring">
                      <option value="">Select a parish</option>
                      {JAMAICA_PARISHES.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={shipping.state} onChange={(e) => setShipping((s) => ({ ...s, state: e.target.value }))} placeholder={shipping.country === 'US' ? 'NY' : 'State or Province'} className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring" />
                  )}
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">
                    {shipping.country === 'JM' ? 'Postal Code (optional)' : 'Postal / ZIP Code'}
                  </label>
                  <input type="text" value={shipping.zip} onChange={(e) => setShipping((s) => ({ ...s, zip: e.target.value }))} placeholder={shipping.country === 'JM' ? 'Kingston 5' : shipping.country === 'US' ? '10001' : 'Postal code'} className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring" />
                </div>
              </div>
            </div>
          }

          {/* Step 2: Payment method — COD via Instagram DM */}
          {currentStep === 2 &&
          <div className="space-y-5">
              <h2 className="font-display text-2xl font-light text-foreground mb-6">Payment Method</h2>
              <div className="p-5 border-2 border-accent bg-accent/5 rounded">
                <div className="flex items-center gap-3 mb-3">
                  <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-accent flex-shrink-0" />
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-foreground">DM to complete payment</span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  After placing your order you&rsquo;ll receive an order number. DM us on Instagram <a href={`https://instagram.com/${IG_HANDLE}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground underline">@{IG_HANDLE}</a> with that number to arrange payment.
                </p>
                <p className="text-sm text-muted-foreground">We&rsquo;ll confirm and ship within 24 hours.</p>
              </div>
            </div>
          }

          {/* Step 3: Review */}
          {currentStep === 3 &&
          <div>
              <h2 className="font-display text-2xl font-light text-foreground mb-6">Review Your Order</h2>
              <div className="space-y-4 mb-6">
                <div className="p-4 bg-secondary">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Contact</p>
                  <p className="text-sm text-foreground">{contact.email || 'guest@example.com'}</p>
                </div>
                <div className="p-4 bg-secondary">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Ship to</p>
                  <p className="text-sm text-foreground">
                    {[shipping.firstName, shipping.lastName].filter(Boolean).join(' ') || '—'}
                    {shipping.address && `, ${shipping.address}`}
                    {shipping.apt && `, ${shipping.apt}`}
                    {shipping.city && `, ${shipping.city}`}
                    {shipping.state && `, ${shipping.state}`}
                    {shipping.zip && ` ${shipping.zip}`}
                    {shipping.country && shipping.country !== 'JM' && `, ${COUNTRIES.find((c) => c.code === shipping.country)?.name || shipping.country}`}
                  </p>
                </div>
                <div className="p-4 bg-secondary">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Payment</p>
                  <p className="text-sm text-foreground">DM @{IG_HANDLE} on Instagram after placing order</p>
                </div>
              </div>
            </div>
          }

          {/* Navigation buttons */}
          <div className="flex gap-3 mt-8">
            {currentStep > 0 &&
            <button
              onClick={() => setCurrentStep((s) => s - 1)}
              className="px-6 py-3.5 border border-border text-sm font-semibold uppercase tracking-widest hover:bg-secondary transition-colors">
              
                ← Back
              </button>
            }
            <button
              onClick={handleNext}
              disabled={submitting}
              className="flex-1 py-3.5 bg-primary text-primary-foreground text-xs font-semibold uppercase tracking-widest hover:bg-accent hover:text-accent-foreground transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed">

              {submitting ? 'Placing order…' : currentStep === steps.length - 1 ? 'Place Order' : 'Continue →'}
            </button>
          </div>
          {stepError && (
            <p className="mt-3 text-[12px] text-red-600 font-medium">✕ {stepError}</p>
          )}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-5">
          <div className="bg-secondary p-6 md:p-8 card-rounded sticky top-28">
            <h3 className="font-display text-xl font-light text-foreground mb-6">Order Summary</h3>

            {/* Free shipping bar */}
            {subtotal < FREE_SHIPPING_THRESHOLD &&
            <div className="mb-6 p-3 bg-background border border-border">
                <p className="text-[11px] text-muted-foreground mb-2">
                  Add <span className="font-semibold text-foreground"><span className="font-sans">J$</span>{(FREE_SHIPPING_THRESHOLD - subtotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> more for free shipping
                </p>
                <div className="h-1 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-accent rounded-full transition-all duration-500" style={{ width: `${subtotal / FREE_SHIPPING_THRESHOLD * 100}%` }} />
                </div>
              </div>
            }
            {subtotal >= FREE_SHIPPING_THRESHOLD &&
            <div className="mb-6 p-3 bg-accent/10 border border-accent/20 flex items-center gap-2">
                <Icon name="CheckCircleIcon" size={16} className="text-accent" />
                <p className="text-[11px] text-foreground font-medium">You qualify for free shipping!</p>
              </div>
            }

            {/* Cart items */}
            <div className="space-y-4 mb-6">
              {cartItems.length === 0 && (
                <p className="text-[13px] text-muted-foreground italic">Your cart is empty. <Link href="/products" className="underline hover:text-foreground">Browse products</Link>.</p>
              )}
              {cartItems.map((item) =>
              <div key={item.id} className="flex gap-3 items-start">
                  <div className="relative w-16 h-16 flex-shrink-0 overflow-hidden rounded">
                    <AppImage src={item.image} alt={item.alt} fill sizes="64px" className="object-cover" />
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-primary text-primary-foreground text-[9px] font-bold rounded-full flex items-center justify-center">
                      {item.qty}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                    <p className="text-[11px] text-muted-foreground">{item.size}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <span className="text-sm font-semibold text-foreground"><span className="font-sans">J$</span>{item.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <button
                      type="button"
                      onClick={() => removeFromCart(item.id as any)}
                      className="text-muted-foreground hover:text-red-600 transition-colors p-1 -m-1"
                      aria-label={`Remove ${item.name}`}>
                      <Icon name="TrashIcon" size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Promo code */}
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePromo(); } }}
                placeholder="Promo code"
                disabled={!!appliedCoupon}
                className="flex-1 border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60" />

              {!appliedCoupon ?
              <button
                onClick={handlePromo}
                disabled={promoChecking || !promoCode.trim()}
                className="px-4 py-2.5 bg-primary text-primary-foreground text-[11px] font-semibold uppercase tracking-wider hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                {promoChecking ? 'Checking…' : 'Apply'}
              </button>
              :
              <button
                onClick={clearCoupon}
                className="px-4 py-2.5 border border-border text-[11px] font-semibold uppercase tracking-wider hover:bg-secondary transition-colors">
                Remove
              </button>
              }
            </div>
            {appliedCoupon &&
            <p className="text-xs text-green-700 font-medium mb-4 flex items-center gap-1.5">
                <Icon name="CheckCircleIcon" size={14} className="text-green-700" />
                Code <span className="uppercase">{appliedCoupon.code}</span> applied
                {appliedCoupon.free_shipping ? ' — free shipping' : ''}
                {appliedCoupon.discount_type === 'percent' && appliedCoupon.amount > 0 ? ` — ${appliedCoupon.amount}% off` : ''}
              </p>
            }
            {promoError &&
            <p className="text-xs text-red-600 font-medium mb-4 flex items-start gap-1.5">
                <Icon name="XCircleIcon" size={14} className="text-red-600 flex-shrink-0 mt-[2px]" />
                <span>{promoError}</span>
              </p>
            }

            {/* Totals */}
            <div className="space-y-3 pt-4 border-t border-border">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-medium"><span className="font-sans">J$</span>{subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              {discount > 0 &&
              <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Discount</span>
                  <span className="font-medium text-green-700">-<span className="font-sans">J$</span>{discount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              }
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Shipping</span>
                <span className="font-medium">{shippingCost === 0 ? 'Free' : <><span className="font-sans">J$</span>{shippingCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>}</span>
              </div>
              <div className="flex justify-between text-base font-semibold pt-3 border-t border-border">
                <span>Total</span>
                <span><span className="font-sans">J$</span>{total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);

}